import React, { useMemo, useState } from 'react';
import { Column, Table } from '@tanstack/react-table';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';

import Checkbox from 'shared/components/atoms/Checkbox';
import Popover from 'shared/components/atoms/Popover';
import { Box, Button, Flex, Text } from 'shared/components/display';

import { BUTTON_VARIANTS } from 'shared/styles/button';
import COLORS from 'shared/styles/colors';

import type { CheckboxFilterValue, TextFilterValue } from './types';

type FilterValue = TextFilterValue | CheckboxFilterValue | undefined;

interface Props<T> {
  column: Column<T, unknown>;
  table: Table<T>;
}

/**
 * Per-column filter popover. The column meta's `filterType` decides the UI:
 *   - 'text'     → contains / does-not-contain free-text input
 *   - 'checkbox' → list of unique values to include
 *
 * Filter state is committed on Apply (not on every keystroke / toggle) so the
 * table doesn't re-filter mid-edit.
 */
const ColumnFilterPopover = <T,>({ column, table }: Props<T>) => {
  const filterType = column.columnDef.meta?.filterType;
  if (!filterType) return null;

  const committedValue = column.getFilterValue() as FilterValue;
  const isActive = isFilterActive(committedValue);

  return (
    <Popover
      placement="bottom-start"
      width={filterType === 'checkbox' ? 220 : 260}
      ClickComponent={
        <Box
          className={cs('brkfst-table__filter-trigger', {
            'brkfst-table__filter-trigger--active': isActive,
          })}
          role="button"
          aria-label="Filter column"
        >
          <FontAwesomeIcon
            icon={faFilter}
            size="xs"
            color={isActive ? COLORS.PRIMARY500 : COLORS.NEUTRAL400}
          />
        </Box>
      }
    >
      {filterType === 'text' ? (
        <TextFilterBody column={column} table={table} />
      ) : (
        <CheckboxFilterBody column={column} table={table} />
      )}
    </Popover>
  );
};

// ---------------------------------------------------------------------------
// Text filter (contains / does-not-contain)
// ---------------------------------------------------------------------------

const TextFilterBody = <T,>({ column }: Props<T>) => {
  const committed = column.getFilterValue() as TextFilterValue | undefined;
  const [mode, setMode] = useState<'contains' | 'notContains'>(
    committed?.mode ?? 'contains',
  );
  const [value, setValue] = useState(committed?.value ?? '');

  const apply = () => {
    if (!value.trim()) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue({ mode, value: value.trim() });
    }
  };

  const clear = () => {
    setMode('contains');
    setValue('');
    column.setFilterValue(undefined);
  };

  return (
    <Box className="brkfst-table__filter-popover" p={3}>
      <Text className="brkfst-table__filter-label">Filter</Text>
      <Flex className="brkfst-table__filter-mode-row">
        <label className="brkfst-table__filter-mode">
          <input
            type="radio"
            checked={mode === 'contains'}
            onChange={() => setMode('contains')}
          />
          <span>Contains</span>
        </label>
        <label className="brkfst-table__filter-mode">
          <input
            type="radio"
            checked={mode === 'notContains'}
            onChange={() => setMode('notContains')}
          />
          <span>Does not contain</span>
        </label>
      </Flex>
      <input
        type="text"
        className="brkfst-table__filter-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Enter text..."
        onKeyDown={(e) => {
          if (e.key === 'Enter') apply();
        }}
      />
      <Flex className="brkfst-table__filter-actions">
        <Button
          variant={BUTTON_VARIANTS.TERTIARY}
          onClick={clear}
          type="button"
        >
          Clear
        </Button>
        <Button
          variant={BUTTON_VARIANTS.MICRO}
          onClick={apply}
          type="button"
        >
          Apply
        </Button>
      </Flex>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Checkbox filter (multi-select from unique values)
// ---------------------------------------------------------------------------

const CheckboxFilterBody = <T,>({ column, table }: Props<T>) => {
  const meta = column.columnDef.meta;
  const committed = (column.getFilterValue() as CheckboxFilterValue | undefined) ?? [];
  const [selected, setSelected] = useState<string[]>(committed);

  // Either use the column-provided filterOptions, or auto-derive from the current
  // (pre-filter) row model so we offer every value the user can see.
  const options = useMemo<string[]>(() => {
    if (meta?.filterOptions?.length) return meta.filterOptions;

    const unique = new Set<string>();
    table.getPreFilteredRowModel().rows.forEach((row) => {
      const raw = meta?.filterAccessor
        ? meta.filterAccessor(row.original)
        : row.getValue(column.id);
      if (raw == null) return;
      unique.add(String(raw));
    });
    return Array.from(unique).sort();
  }, [meta, table, column.id]);

  const toggle = (option: string) => {
    setSelected((prev) =>
      prev.includes(option) ? prev.filter((v) => v !== option) : [...prev, option],
    );
  };

  const selectAll = () => setSelected(options);
  const clear = () => {
    setSelected([]);
    column.setFilterValue(undefined);
  };
  const apply = () => {
    column.setFilterValue(selected.length ? selected : undefined);
  };

  return (
    <Box className="brkfst-table__filter-popover" p={3}>
      <Flex className="brkfst-table__filter-shortcuts">
        <Button
          variant={BUTTON_VARIANTS.TERTIARY}
          onClick={selectAll}
          type="button"
        >
          Select all
        </Button>
        <Button
          variant={BUTTON_VARIANTS.TERTIARY}
          onClick={clear}
          type="button"
        >
          Clear
        </Button>
      </Flex>
      <Box className="brkfst-table__filter-options">
        {options.length === 0 && (
          <Text className="brkfst-table__filter-empty">No values</Text>
        )}
        {options.map((option) => (
          <label key={option} className="brkfst-table__filter-option">
            <Checkbox
              name={`filter-${column.id}-${option}`}
              checked={selected.includes(option)}
              onChange={() => toggle(option)}
            />
            <span title={option}>{option}</span>
          </label>
        ))}
      </Box>
      <Flex className="brkfst-table__filter-actions">
        <Button variant={BUTTON_VARIANTS.MICRO} onClick={apply} type="button">
          Apply
        </Button>
      </Flex>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isFilterActive = (value: FilterValue): boolean => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'object' && 'value' in value && value.value.length > 0;
};

export default ColumnFilterPopover;
